/**
 * Which files may become context documents of a conversation — shared by the chat and the tender
 * board's „Dateien" section, so the two cannot drift apart.
 *
 * The server does not filter by type (tri-server `contextDocumentsUpload`), so this is the gate.
 */

// Allowed file types (aligned with tri-server context-documents)
//
// The extension list is the real gate: `isValidContextFile` accepts an extension OR a MIME type,
// and a CAD drawing dragged off the desktop usually arrives with an empty or octet-stream type.
// The CAD MIME types below exist for the *dragover* check, which can only see `item.type` (the
// filename is not exposed yet) — without them the "drop it here" overlay stays hidden whenever
// the OS does report a real DWG type.
export const allowedFileTypes = [
    '.pdf',
    '.docx',
    '.txt',
    '.csv',
    '.xlsx',
    '.pptx',
    '.html',
    // A tender notice as the procurement platforms hand it out (eForms); read as text, like HTML.
    '.xml',
    '.md',
    '.jpg',
    '.jpeg',
    '.png',
    '.zip',
    '.dwg',
    '.dxf',
    // Outlook mail: the server keeps the original and unpacks its text and attachments next to it.
    '.msg',
];
export const allowedMimeTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/html',
    'application/xml',
    'text/xml',
    'text/markdown',
    'image/jpeg',
    'image/png',
    'application/zip',
    // CAD: the registered types plus the variants AutoCAD installs put on the clipboard.
    'image/vnd.dwg',
    'image/x-dwg',
    'application/acad',
    'image/vnd.dxf',
    'application/dxf',
    // What Windows reports for a .msg — needed for the dragover check, like the CAD types above.
    'application/vnd.ms-outlook',
];
export const contextUploadAccept = [...allowedFileTypes, ...allowedMimeTypes].join(',');

export const isValidContextFile = (file: Pick<File, 'name' | 'type'>): boolean => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    const hasValidExtension = allowedFileTypes.some(ext =>
        fileName.endsWith(ext.toLowerCase())
    );

    const hasValidMimeType = allowedMimeTypes.some(mimeType =>
        fileType === mimeType
    );

    return hasValidExtension || hasValidMimeType;
};

// Mirrors the server's rendering of the same limit (tri-server src/uploadErrors.ts).
export const formatMegabytes = (bytes: number): string => `${Math.round(bytes / (1024 * 1024))} MB`;

/**
 * Split a selection into what can be uploaded and what cannot, and why. A file that is not sent
 * must be named to the user — filtering it out quietly is how rejected files used to vanish.
 *
 * `maxBytes` is the server's limit when it could be loaded; without it nothing is refused for size
 * here and the server's 413 decides.
 */
export const sortUploadCandidates = <T extends Pick<File, 'name' | 'type' | 'size'>>(
    files: T[],
    maxBytes: number | null,
): { valid: T[]; unsupported: T[]; tooLarge: T[] } => {
    const valid: T[] = [];
    const unsupported: T[] = [];
    const tooLarge: T[] = [];
    for (const file of files) {
        if (!isValidContextFile(file)) unsupported.push(file);
        else if (maxBytes && file.size > maxBytes) tooLarge.push(file);
        else valid.push(file);
    }
    return { valid, unsupported, tooLarge };
};
